/**
 * Resolves the canonical Google Maps URL for a place.
 *
 * This is the ONLY booking-adjacent link we can stand behind. Google Places exposes no
 * reservation URI of any kind (verified against a field-mask `*` Place Details response:
 * 60+ fields, none of them a booking link), and Reserve-with-Google is a partner feed for
 * booking providers rather than something we can query. What the Maps place page DOES carry
 * is Google's own Reserve button, wired to whichever partner the venue uses — OpenTable,
 * Resy, SevenRooms, Tock. So landing the client on Maps hands them a working reservation
 * path that Google maintains, instead of a platform slug the model guessed at. (Two of the
 * three OpenTable links one production research call produced were 404s.)
 *
 * On a phone the link opens the Maps app rather than a browser, and from there the client
 * gets directions, hours, photos, reviews, and a call button.
 *
 * Two forms, in preference order:
 *  1. `googleMapsLinks.placeUri` — what Google hands us on the Places response.
 *  2. `?q=place_id:` — Google's documented universal form, for records stored before
 *     `googleMapsLinks` joined the field mask.
 *
 * Important for UI copy: the Reserve button only renders for venues Google has a booking
 * partner for, and NO API field tells us which case a given place is in. Label the action
 * neutrally ("Reserve · Google Maps", "Open in Maps") — never "Book a table", which we
 * cannot promise. For non-partner venues the same page still carries hours, phone, and the
 * website, which is where the client needed to land anyway.
 *
 * @example
 * placeMapsUrl({ googleMapsLinks: { placeUri: 'https://maps.google.com/?cid=123&g_mp=Ci…' } });
 * // 'https://maps.google.com/?cid=123'
 */
export interface PlaceMapsUrlInput {
  ids?: { googlePlaceId?: string | null } | null;
  googleMapsLinks?: { placeUri?: string | null } | null;
}

/**
 * Drops Google's `g_mp` analytics blob from a Maps URL.
 *
 * Google returns `placeUri` with a ~60-character tracking parameter appended. The `cid`
 * alone resolves to the same place, and that parameter is pure noise — in a text message it
 * more than doubles the visible length of the link, and in a card it bloats the href for no
 * benefit. Anything unexpected is returned untouched rather than guessed at.
 */
function stripMapsTracking(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete('g_mp');
    return parsed.toString();
  } catch {
    return url;
  }
}

export function placeMapsUrl(place: PlaceMapsUrlInput): string | undefined {
  const placeUri = place.googleMapsLinks?.placeUri;
  if (placeUri) return stripMapsTracking(placeUri);

  const googlePlaceId = place.ids?.googlePlaceId;
  if (googlePlaceId) return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(googlePlaceId)}`;

  return;
}
